// src/context/AirbnbAuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const AirbnbAuthContext = createContext();

export function useAirbnbAuth() {
  const context = useContext(AirbnbAuthContext);
  if (!context) {
    throw new Error('useAirbnbAuth must be used within AirbnbAuthProvider');
  }
  return context;
}

export function AirbnbAuthProvider({ children }) {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Carregar token do localStorage ao iniciar
  useEffect(() => {
    const storedToken = localStorage.getItem('airbnb_token');
    if (storedToken) {
      setToken(storedToken);
      fetchClientInfo(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  // Buscar informações do cliente logado
  const fetchClientInfo = async (authToken) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/airbnb/auth/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setClient(data.client);
      } else {
        // Token inválido, limpar
        localStorage.removeItem('airbnb_token');
        setToken(null);
        setClient(null);
      }
    } catch (error) {
      console.error('Error fetching client info:', error);
      localStorage.removeItem('airbnb_token');
      setToken(null);
      setClient(null);
    } finally {
      setLoading(false);
    }
  };

  // Registrar novo cliente
  const register = async (name, email, phone, address, password, inviteToken) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/airbnb/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, email, phone, address, password, inviteToken })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('airbnb_token', data.token);
        setToken(data.token);
        setClient(data.client);
        return { success: true, data };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: error.message };
    }
  };

  // Login
  const login = async (email, password) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/airbnb/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('airbnb_token', data.token);
        setToken(data.token);
        setClient(data.client);
        return { success: true, data };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  // Logout
  const logout = async () => {
    try {
      if (token) {
        await fetch(`${process.env.REACT_APP_API_URL}/api/airbnb/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('airbnb_token');
      setToken(null);
      setClient(null);
    }
  };

  const value = {
    client,
    token,
    loading,
    register,
    login,
    logout,
    isAuthenticated: !!client
  };

  return (
    <AirbnbAuthContext.Provider value={value}>
      {children}
    </AirbnbAuthContext.Provider>
  );
}
